// Multi-engine keyword rank table — Core design tokens only, no chart lib.
// Self-contained: no imports from sibling client-analytics files, so this
// frame can be reviewed/dropped in on its own.
//
// Pattern showcased: the AA "per-cell change badge" — a rank position with a
// small inline ↑/↓ Δ badge riding alongside it when a change value is known.
//
// Rank semantics: LOWER numbers are BETTER (position #1 beats position #10).
// `google_change` is a pre-signed "positions gained" value, matching the AA
// REST convention: +2 means the keyword moved UP two spots (rank number went
// DOWN) — that's an improvement, badge is green with an up arrow. A negative
// change means the keyword slid down — badge is red with a down arrow. Zero
// means no movement. Null/undefined means "no change data for this cell" and
// renders no badge at all.

import { useMemo, useState, type ReactNode } from "react";

// Deliberately duplicates types/clientAnalytics.ts's MultiEngineRankRow
// rather than importing it (this file stays import-free from sibling
// client-analytics files, see header comment) -- keep every field in sync
// with that type by hand. `location` was narrower here (`string`, no
// `null`) than the canonical type (`string | null`) until P2-8: AA's raw
// payload always sends the location key, `null` for a non-geo-targeted
// keyword, so `location: null` is a real, expected value, not an edge
// case -- the render path (`row.location ?? "—"`) already handled it
// safely via `??`, this was a type-only gap, not a runtime bug.
//
// `keyword_id` is OPTIONAL, still -- but landed on the live schema
// 2026-08-09 (commit 78fc402c6): AA's own numeric rank-tracker keyword id,
// confirmed live against ALIBI Music (35 keyword phrases each tracked
// TWICE, 70 total rows, "adobe premiere pro music extension" is both
// keyword_id 75567914 and 75663705 with identical location). It stays
// Optional only because a snapshot built before that commit won't carry it
// yet -- for any current pull, `rowKey()` below prefers it; the composite-
// of-every-field fallback (stable across re-sorts, unlike an array index)
// exists purely for that pre-rebuild-snapshot case, not as the normal path.
export interface RankRow {
  keyword: string;
  keyword_id?: string | null;
  location?: string | null;
  google?: number | null;
  google_change?: number | null;
  google_mobile?: number | null;
  google_places?: number | null;
  bing?: number | null;
  volume?: number | null;
}

export interface MultiEngineRankTableProps {
  rows: RankRow[];
  /**
   * Total row count BEFORE any cap was applied (e.g. AA's own "Showing 50
   * of 70 Rows" caption) -- `MultiEngineRankSection.total_rows`, landed on
   * the live schema 2026-08-09 (commit 78fc402c6), confirmed exact against
   * ALIBI Music (50 of 70). Optional and defaults to `rows.length` only for
   * a snapshot built before that commit, so this never regresses to a
   * fabricated total on an older payload -- "Showing 50 of 50" in that one
   * case, not a guess.
   */
  totalCount?: number;
}

// Stable, order-independent fallback identity for a row -- see the
// `keyword_id` doc comment above. Every ranking field is part of the key so
// two same-keyword/same-location rows that genuinely differ (e.g. a
// mobile-only rank change) still don't collide.
function rowKey(row: RankRow, index: number): string {
  if (row.keyword_id) return row.keyword_id;
  return [
    row.keyword,
    row.location ?? "",
    row.google ?? "",
    row.google_change ?? "",
    row.google_mobile ?? "",
    row.google_places ?? "",
    row.bing ?? "",
    row.volume ?? "",
    index, // last-resort tiebreak for two fully-identical rows
  ].join("::");
}

// AA renders rank positions as ordinals ("1ˢᵗ", "2ⁿᵈ", "5ᵗʰ"), not plain
// integers -- returns the number plus its suffix as separate pieces so the
// caller can render the suffix as a smaller superscript, matching the
// reference typography.
function ordinalParts(n: number): { number: number; suffix: string } {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return { number: n, suffix: "th" };
  switch (n % 10) {
    case 1:
      return { number: n, suffix: "st" };
    case 2:
      return { number: n, suffix: "nd" };
    case 3:
      return { number: n, suffix: "rd" };
    default:
      return { number: n, suffix: "th" };
  }
}

// 6 realistic, varied demo rows: a clean improve, a drop, a flat #2 (already
// near the top), a keyword with no Google desktop rank yet but a Places rank,
// a small-volume long-tail win, and a DUPLICATE keyword text tracked under a
// second location (AA reference: "adobe premiere pro music extension" is
// tracked twice, ranking differently per location) -- exercises rowKey()'s
// fallback identity, since neither demo row here carries a `keyword_id`.
// Also mixes null ranks, a zero change, and both badge directions so every
// RankCell branch is exercised.
export const demoRanks: RankRow[] = [
  {
    keyword: "water heater repair",
    location: "San Antonio, TX",
    google: 4,
    google_change: 2,
    google_mobile: 5,
    google_places: 2,
    bing: 7,
    volume: 880,
  },
  {
    keyword: "water heater repair",
    location: "Austin, TX",
    google: 11,
    google_change: -1,
    google_mobile: 13,
    google_places: null,
    bing: 16,
    volume: 210,
  },
  {
    keyword: "tankless water heater installation",
    location: "Austin, TX",
    google: 9,
    google_change: -3,
    google_mobile: 11,
    google_places: null,
    bing: 14,
    volume: 320,
  },
  {
    keyword: "emergency plumber near me",
    location: "San Antonio, TX",
    google: 2,
    google_change: 0,
    google_mobile: 3,
    google_places: 1,
    bing: 4,
    volume: 1600,
  },
  {
    keyword: "water heater replacement cost",
    location: "New Braunfels, TX",
    google: null,
    google_change: null,
    google_mobile: null,
    google_places: 8,
    bing: null,
    volume: 210,
  },
  {
    keyword: "gas water heater repair",
    location: "San Antonio, TX",
    google: 6,
    google_change: 4,
    google_mobile: 6,
    google_places: 3,
    bing: 9,
    volume: 140,
  },
];

function formatVolume(n?: number | null): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString();
}

// A rank position plus its optional Δ badge. Improve = number went DOWN.
function RankCell({
  position,
  change,
}: {
  position?: number | null;
  change?: number | null;
}) {
  // `0` is treated the same as null/undefined -- a real rank position is
  // never 0 (ranks start at #1), so this can never hide a legitimate value.
  // Belt-and-suspenders: the backend already resolves "not ranking" to null
  // before this ever renders (aa_rest_shapes._engine_rank, P1-8 -- 50.3% of
  // 3,311 real rows), but rank is lower-is-better, so a literal 0 slipping
  // through unchecked would read as BETTER than #1. This was a live
  // client-facing defect; the check stays even though the contract says it
  // shouldn't be reachable.
  if (position === null || position === undefined || position === 0) {
    return <span className="tabular text-muted-foreground">—</span>;
  }

  let badge: ReactNode = null;
  if (change !== null && change !== undefined) {
    if (change > 0) {
      badge = (
        <span
          className="inline-flex items-center gap-0.5 rounded-full bg-[hsl(var(--status-success-bg))] px-1.5 py-0.5 text-[10px] font-bold leading-none text-[hsl(var(--status-success))]"
          title={`Improved ${change} position${change === 1 ? "" : "s"}`}
        >
          <span aria-hidden="true">↑</span>
          {change}
        </span>
      );
    } else if (change < 0) {
      const dropped = Math.abs(change);
      badge = (
        <span
          className="inline-flex items-center gap-0.5 rounded-full bg-[hsl(var(--status-error-bg))] px-1.5 py-0.5 text-[10px] font-bold leading-none text-[hsl(var(--status-error))]"
          title={`Dropped ${dropped} position${dropped === 1 ? "" : "s"}`}
        >
          <span aria-hidden="true">↓</span>
          {dropped}
        </span>
      );
    } else {
      badge = (
        <span
          className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-bold leading-none text-muted-foreground"
          title="No change"
        >
          •
        </span>
      );
    }
  }

  const { number, suffix } = ordinalParts(position);
  return (
    <span className="inline-flex items-center justify-center gap-1.5">
      <span className="tabular font-semibold text-foreground">
        {number}
        <sup className="text-[9px]">{suffix}</sup>
      </span>
      {badge}
    </span>
  );
}

type SortDirection = "asc" | "desc";

// Google rank, ascending by default (AA reference default: best rank
// first). Lower is better -- see the module docstring's rank-semantics
// note -- so "ascending" means position 1 before position 50. Rows with no
// Google rank (`null`/`undefined`, i.e. not currently ranking) always sort
// to the bottom regardless of direction: a missing rank isn't "better than
// #1" or "worse than #999", it's simply not comparable, matching the same
// "never let a missing value read as a real one" rule as the 0-vs-null fix.
function sortByGoogle(rows: RankRow[], direction: SortDirection): RankRow[] {
  const withRank = rows.filter((r) => r.google !== null && r.google !== undefined);
  const withoutRank = rows.filter((r) => r.google === null || r.google === undefined);
  withRank.sort((a, b) => {
    const delta = (a.google as number) - (b.google as number);
    return direction === "asc" ? delta : -delta;
  });
  return [...withRank, ...withoutRank];
}

export function MultiEngineRankTable({ rows, totalCount }: MultiEngineRankTableProps) {
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const sortedRows = useMemo(() => sortByGoogle(rows, sortDirection), [rows, sortDirection]);
  const shown = rows.length;
  const total = totalCount ?? shown;

  function toggleSort() {
    setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
  }

  // House wording is "Showing {n} of {m}" -- no unit word. But a complete
  // (uncapped) set of exactly one row -- real, live example: Forestry
  // Rentals' multi_engine_ranks -- renders "Showing 1 of 1", which reads as
  // a loading failure ("shouldn't there be more?"), not confirmation the
  // pull completed. Carl's ruling: thin data still shows (never hide the
  // row itself), but state that plainly instead of a 1-of-1 fraction that
  // looks broken.
  const isSingleCompleteRow = shown === total && total <= 1;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {rows.length > 0 && (
        <div className="px-4 py-2 text-dash-context text-muted-foreground border-b border-border">
          {isSingleCompleteRow ? "Only one tracked keyword — showing all of it" : `Showing ${shown} of ${total}`}
        </div>
      )}
      <div className="max-h-[420px] overflow-y-auto">
        <table className="w-full border-collapse text-dash-subtitle">
          <thead className="sticky top-0 z-10 bg-muted">
            <tr className="border-b border-border">
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Keyword
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Location
              </th>
              <th className="px-4 py-2.5 text-center text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                <button
                  type="button"
                  onClick={toggleSort}
                  className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground"
                  aria-label={`Sort by Google rank, currently ${sortDirection === "asc" ? "best first" : "worst first"}`}
                >
                  Google
                  <span aria-hidden="true">{sortDirection === "asc" ? "▲" : "▼"}</span>
                </button>
              </th>
              <th className="px-4 py-2.5 text-center text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Mobile
              </th>
              <th className="px-4 py-2.5 text-center text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Places
              </th>
              <th className="px-4 py-2.5 text-center text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Bing
              </th>
              <th className="px-4 py-2.5 text-right text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Vol/mo
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-6 text-center text-dash-context text-muted-foreground"
                >
                  No ranking data yet.
                </td>
              </tr>
            ) : (
              sortedRows.map((row, i) => (
                <tr
                  key={rowKey(row, i)}
                  className={`border-b border-border last:border-0 transition-colors hover:bg-muted/60 ${
                    i % 2 === 1 ? "bg-muted/30" : "bg-card"
                  }`}
                >
                  <td className="px-4 py-2.5 font-medium text-foreground whitespace-nowrap">
                    {row.keyword}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">
                    {row.location ?? "—"}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <RankCell position={row.google} change={row.google_change} />
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <RankCell position={row.google_mobile} />
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <RankCell position={row.google_places} />
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <RankCell position={row.bing} />
                  </td>
                  <td className="px-4 py-2.5 text-right tabular text-foreground whitespace-nowrap">
                    {formatVolume(row.volume)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
